import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Check, ChevronLeft, ChevronRight, Mail, RefreshCw,
  HelpCircle, Download, ShoppingCart, ArrowRight, FileSignature, Package, PartyPopper,
  Search, AlertCircle, MapPin
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '@/components/ui/sheet';
import { managementService, Attachment, Kit, isLegacyD4SignAttachment } from '@/services/management.service';
import { saleService } from '@/services/sale.service';
import { userService } from '@/services/user.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CheckoutData {
  id?: string;
  url?: string;
  expires_at?: string;
  [key: string]: any;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://xdoinfinitto.com.br:3000';
const WHATSAPP_PHONE = '5545991559222';
const RESEND_COOLDOWN_MINUTES = 60;

const PrimeiroAcesso: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const preRegisterId = Number(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const userData = JSON.parse(localStorage.getItem('userlogged') || '{}');
  const firstName = userData?.nome_razao_social?.split(' ')[0] || '';

  const [loading, setLoading] = useState(true);
  const [attachmentsList, setAttachmentsList] = useState<Attachment[]>([]);
  const [kits, setKits] = useState<Kit[]>([]);
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<Record<number, number>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [kitDetailOpen, setKitDetailOpen] = useState(false);
  const [kitDetailData, setKitDetailData] = useState<Kit | null>(null);
  const [kitDetailAttachment, setKitDetailAttachment] = useState<Attachment | null>(null);

  // Completar Cadastro - Step 1
  const [cadastroCompleted, setCadastroCompleted] = useState(!!userData?.address);
  const [tipoChave, setTipoChave] = useState('');
  const [chave_pix, setChavePix] = useState('');
  const [maxLengthChavePix, setMaxLengthChavePix] = useState(0);
  const [placeHolderChavePix, setPlaceHolderChavePix] = useState('Digite a chave');
  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [searchingCep, setSearchingCep] = useState(false);
  const [cepError, setCepError] = useState('');

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const attachments: Attachment[] = await managementService.getAttachments(preRegisterId);
      setAttachmentsList(attachments);

      const kitAttachment = attachments.find(
        (a) => a.attachmentType === 'recibo_kit'
      );

      if (kitAttachment) {
        const kitsData: Kit[] = await managementService.getKits();
        setKits(kitsData);

        const savedCheckout = localStorage.getItem('checkoutData');
        if (savedCheckout) {
          try {
            const parsed: CheckoutData = JSON.parse(savedCheckout);
            if (parsed?.expires_at && new Date(parsed.expires_at) > new Date()) {
              setCheckoutData(parsed);
            } else {
              localStorage.removeItem('checkoutData');
            }
          } catch {
            localStorage.removeItem('checkoutData');
          }
        }

        if (
          kitAttachment.situacao === 'aguardando_pagamento' &&
          kitAttachment.url &&
          !savedCheckout
        ) {
          try {
            const saleDetail = await saleService.getSaleDetail(kitAttachment.url);
            if (saleDetail?.data) {
              setCheckoutData(saleDetail.data);
              localStorage.setItem('checkoutData', JSON.stringify(saleDetail.data));
            }
          } catch (e) {
            console.error('Erro ao recuperar checkout:', e);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [preRegisterId]);

  useEffect(() => {
    if (preRegisterId) {
      loadAttachments();
    }
  }, [preRegisterId, loadAttachments]);

  const isStepUnlocked = (index: number): boolean => {
    if (!cadastroCompleted) return false;
    if (index === 0) return true;
    const prev = attachmentsList[index - 1];
    return prev?.situacao === 'assinado' || prev?.situacao === 'pago';
  };

  const isStepCompleted = (attachment: Attachment): boolean => {
    return attachment.situacao === 'assinado' || attachment.situacao === 'pago';
  };

  const completedCount = attachmentsList.filter((a) => isStepCompleted(a)).length;
  const totalSteps = attachmentsList.length;
  const allStepsCompleted = totalSteps > 0 && completedCount === totalSteps && cadastroCompleted;

  // ---- Completar Cadastro functions ----
  const changeTipoChave = (tipoSelected: string) => {
    setTipoChave(tipoSelected);
    setChavePix('');
    setMaxLengthChavePix(0);
    setPlaceHolderChavePix('Digite a chave');
    if (tipoSelected === 'cpf_cnpj') {
      setMaxLengthChavePix(18);
      setPlaceHolderChavePix('Digite o CPF/CNPJ');
    } else if (tipoSelected === 'email') {
      setMaxLengthChavePix(1000);
      setPlaceHolderChavePix('Digite o e-mail');
    } else if (tipoSelected === 'phone') {
      setMaxLengthChavePix(15);
      setPlaceHolderChavePix('Digite o telefone');
    } else if (tipoSelected === 'random') {
      setMaxLengthChavePix(1000);
      setPlaceHolderChavePix('Digite a chave aleatória');
    }
  };

  const formataCep = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const aplicarMascaraChavePix = (value: string) => {
    if (tipoChave === 'cpf_cnpj') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 11) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else {
        return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
    }
    if (tipoChave === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const buscarCep = async () => {
    if (!cep || cep.length < 8) {
      setCepError('CEP deve ter 8 dígitos');
      return;
    }
    setSearchingCep(true);
    setCepError('');
    try {
      const resp = await userService.searchCep(cep);
      if (resp && resp.erro === 'true') {
        setCepError('CEP não encontrado, verifique e tente novamente');
        return;
      }
      setUf(resp.uf);
      setCidade(resp.localidade);
      setLogradouro(`${resp.bairro}, ${resp.logradouro}`);
    } catch {
      setCepError('Erro ao buscar CEP');
    } finally {
      setSearchingCep(false);
    }
  };

  const isCadastroFormValid = () => {
    return tipoChave && chave_pix && cep && cidade && uf && logradouro && numero;
  };

  const handleSubmitCadastro = async () => {
    setBtnDisabled(true);
    if (!userData?.id) {
      toast({ title: 'Erro', description: 'Dados do usuário não encontrados', variant: 'destructive' });
      setBtnDisabled(false);
      return;
    }
    const data = {
      id: userData.id,
      pix: chave_pix,
      zip_code: cep,
      line_1: logradouro,
      city: cidade,
      state: uf,
      number: numero,
      complement: complemento,
    };
    try {
      const resp = await userService.atualizarCadastroIncompleto(data);
      if (resp && resp.message) {
        const newData = { ...userData, address: data };
        localStorage.setItem('userlogged', JSON.stringify(newData));
        toast({ title: 'Sucesso', description: 'Cadastro atualizado com sucesso' });
        setCadastroCompleted(true);
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar cadastro, tente novamente ou contate o suporte',
        variant: 'destructive',
      });
    } finally {
      setBtnDisabled(false);
    }
  };

  const renderCadastroStep = () => {
    if (cadastroCompleted) {
      return (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-green-800">Cadastro completo!</span>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm text-foreground">
            Insira sua chave PIX e endereço para completar seu cadastro.
          </p>
        </div>

        {/* Chave PIX */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold">
            Sua chave PIX <span className="text-red-500">*</span>
          </Label>
          <Select value={tipoChave} onValueChange={changeTipoChave}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo de chave*" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cpf_cnpj">CPF/CNPJ</SelectItem>
              <SelectItem value="phone">Telefone</SelectItem>
              <SelectItem value="email">E-mail</SelectItem>
              <SelectItem value="random">Chave Aleatória</SelectItem>
            </SelectContent>
          </Select>
          <Input
            disabled={!tipoChave}
            value={chave_pix}
            onChange={(e) => setChavePix(aplicarMascaraChavePix(e.target.value))}
            maxLength={maxLengthChavePix || undefined}
            placeholder={placeHolderChavePix + '*'}
          />
        </div>

        {/* CEP */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold">
            CEP <span className="text-red-500">*</span>
          </Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                value={cep}
                onChange={(e) => {
                  setCep(formataCep(e.target.value));
                  setCepError('');
                }}
                maxLength={9}
                placeholder="12345-678"
              />
            </div>
            <Button
              onClick={buscarCep}
              disabled={searchingCep || cep.length < 8}
              className="bg-primary hover:bg-primary-hover"
            >
              {searchingCep ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </Button>
          </div>
          {cepError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{cepError}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Endereço após buscar CEP */}
        {cidade && uf && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm">
                <strong>Cidade/UF:</strong> {cidade} - {uf}
              </p>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">
                  Endereço <span className="text-red-500">*</span>
                </Label>
                <Input value={logradouro} onChange={(e) => setLogradouro(e.target.value)} placeholder="Logradouro" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">
                  Número <span className="text-red-500">*</span>
                </Label>
                <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Número" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Complemento</Label>
                <Input value={complemento} onChange={(e) => setComplemento(e.target.value)} placeholder="Complemento/referência" />
              </div>
            </CardContent>
          </Card>
        )}

        <Button
          onClick={handleSubmitCadastro}
          disabled={!isCadastroFormValid() || btnDisabled}
          className="w-full bg-primary hover:bg-primary-hover"
        >
          {btnDisabled ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Enviando, aguarde...
            </>
          ) : (
            'Salvar e continuar'
          )}
        </Button>
      </div>
    );
  };
  const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  const resendEmailSign = async (attachment: Attachment) => {
    const lastSent = localStorage.getItem('lastSendEmailSign');
    if (lastSent) {
      const diffMinutes = (Date.now() - new Date(lastSent).getTime()) / 1000 / 60;
      if (diffMinutes < RESEND_COOLDOWN_MINUTES) {
        toast({
          title: 'Aguarde',
          description: `Você já reenviou o e-mail recentemente. Aguarde ${Math.ceil(RESEND_COOLDOWN_MINUTES - diffMinutes)} minutos.`,
          variant: 'destructive',
        });
        return;
      }
    }

    setActionLoading(`resend-${attachment.id}`);
    try {
      if (isLegacyD4SignAttachment(attachment)) {
        await managementService.resendEmailSignLegacy(attachment.id);
      } else {
        await managementService.resendEmailSign(attachment.id);
      }
      localStorage.setItem('lastSendEmailSign', new Date().toISOString());
      toast({
        title: 'Sucesso',
        description: 'E-mail reenviado com sucesso! Verifique sua caixa de entrada.',
      });
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível reenviar o e-mail.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const checkContractSigned = async (attachment: Attachment) => {
    setActionLoading(`check-${attachment.id}`);
    try {
      const response = isLegacyD4SignAttachment(attachment)
        ? await managementService.checkContractSignedLegacy(attachment.id)
        : await managementService.checkContractSigned(attachment.id);
      if (response.success) {
        toast({
          title: 'Contrato verificado!',
          description: 'Sua assinatura foi detectada. Em breve você poderá prosseguir.',
        });
        setAttachmentsList((prev) =>
          prev.map((a) =>
            a.id === attachment.id ? { ...a, situacao: 'verificando_assinatura' } : a
          )
        );
      } else {
        toast({
          title: 'Assinatura não detectada',
          description: 'Ainda não detectamos sua assinatura. Verifique seu e-mail e assine o contrato.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao verificar assinatura.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const openHelp = (attachment: Attachment) => {
    const message = `Olá, preciso de ajuda com meu contrato (Referência: ${attachment.name || attachment.id})`;
    window.open(
      `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const downloadContract = async (attachment: Attachment) => {
    if (!attachment.path) return;
    if (attachment.path === 'clicksign') {
      try {
        const url = await managementService.getDownloadUrl(attachment);
        if (!url) {
          toast({
            title: 'Erro',
            description: 'Erro ao obter link de download',
            variant: 'destructive',
          });
          return;
        }
        window.open(url, '_blank');
      } catch {
        toast({
          title: 'Erro',
          description: 'Erro ao obter link de download',
          variant: 'destructive',
        });
      }
      return;
    }
    window.open(attachment.path, '_blank');
  };

  const getKitImages = (kit: Kit): string[] => {
    return [kit.imageOnePath, kit.imageTwoPath, kit.imageThreePath]
      .filter((img): img is string => !!img);
  };

  const getKitImageUrl = (kit: Kit, index: number): string => {
    const images = getKitImages(kit);
    if (images.length === 0) return '';
    const safeIndex = ((index % images.length) + images.length) % images.length;
    return `${API_URL}/pre-register/kit/item-image/${images[safeIndex]}`;
  };

  const prevImage = (kitId: number) => {
    setCurrentImageIndex((prev) => ({
      ...prev,
      [kitId]: (prev[kitId] || 0) - 1,
    }));
  };

  const nextImage = (kitId: number) => {
    setCurrentImageIndex((prev) => ({
      ...prev,
      [kitId]: (prev[kitId] || 0) + 1,
    }));
  };

  const openKitDetail = (kit: Kit, attachment: Attachment) => {
    setKitDetailData(kit);
    setKitDetailAttachment(attachment);
    setKitDetailOpen(true);
  };

  const createCheckout = async (attachment: Attachment) => {
    if (!kitDetailData) return;

    setActionLoading('checkout');
    try {
      const response = await saleService.createCheckoutKitMaleta({
        kitId: kitDetailData.id,
        kitName: kitDetailData.name,
        kitPrice: kitDetailData.price,
        installments: 1,
        codigo_revendedora: userData.codigo,
        nome_razao_social: userData.nome_razao_social,
        id_revendedora: userData.id,
        pre_register_id: preRegisterId,
        pre_register_attachment_id: attachment.id,
      });

      if (response.success && response.data) {
        localStorage.setItem('checkoutData', JSON.stringify(response.data));
        setCheckoutData(response.data);
        setKitDetailOpen(false);

        if (response.data.url) {
          window.open(response.data.url, '_blank');
        }

        await loadAttachments();
      } else {
        throw new Error('Resposta inválida');
      }
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao criar checkout. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const checkKitPayed = async (attachment: Attachment) => {
    setActionLoading(`kit-check-${attachment.id}`);
    try {
      const response = await saleService.checkKitPayed(attachment.id);
      if (response.success) {
        localStorage.removeItem('checkoutData');
        setCheckoutData(null);
        toast({
          title: 'Pagamento confirmado!',
          description: 'Seu pagamento foi confirmado com sucesso!',
        });
        await loadAttachments();
      } else {
        toast({
          title: 'Pagamento não detectado',
          description: 'Ainda não detectamos seu pagamento. Tente novamente em alguns minutos.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao verificar pagamento.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const goToCheckout = () => {
    if (!checkoutData) return;

    if (checkoutData.expires_at) {
      const expiresAt = new Date(checkoutData.expires_at);
      if (expiresAt < new Date()) {
        localStorage.removeItem('checkoutData');
        setCheckoutData(null);
        toast({
          title: 'Checkout expirado',
          description: 'O link de pagamento expirou. Escolha o kit novamente.',
          variant: 'destructive',
        });
        loadAttachments();
        return;
      }
    }

    if (checkoutData.url) {
      window.open(checkoutData.url, '_blank');
    }
  };

  const changeKit = async (attachment: Attachment) => {
    setActionLoading(`change-kit-${attachment.id}`);
    try {
      await managementService.changeKit(attachment.id);
      localStorage.removeItem('checkoutData');
      setCheckoutData(null);
      toast({
        title: 'Kit alterado',
        description: 'Agora você pode escolher outro kit.',
      });
      await loadAttachments();
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao trocar kit.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const openHelpKit = (kitName: string) => {
    const message = `Olá, preciso de ajuda com o kit: ${kitName}`;
    window.open(
      `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStepIcon = (attachment: Attachment) => {
    if (attachment.attachmentType === 'contrato') return FileSignature;
    if (attachment.attachmentType === 'recibo_kit') return Package;
    return Check;
  };

  const renderContractStep = (attachment: Attachment) => {
    if (attachment.situacao === 'aguardando_assinaturas') {
      return (
        <div className="space-y-4">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              Realize a assinatura do contrato e envio dos documentos necessários, confira seu e-mail: {userData.email}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => checkContractSigned(attachment)}
              disabled={actionLoading === `check-${attachment.id}`}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading === `check-${attachment.id}` ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Já assinei
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => resendEmailSign(attachment)}
              disabled={actionLoading === `resend-${attachment.id}`}
            >
              {actionLoading === `resend-${attachment.id}` ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Reenviar E-mail
            </Button>
            <Button variant="ghost" size="sm" onClick={() => openHelp(attachment)}>
              <HelpCircle className="w-4 h-4 mr-2" /> Ajuda
            </Button>
          </div>
        </div>
      );
    }

    if (attachment.situacao === 'verificando_assinatura') {
      return (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Estamos analisando os dados de assinatura, em breve você poderá prosseguir.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadAttachments()}>
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>
      );
    }

    if (attachment.situacao === 'assinado') {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">Contrato assinado com sucesso</span>
          </div>
          {attachment.path && (
            <Button variant="outline" size="sm" onClick={() => downloadContract(attachment)}>
              <Download className="w-4 h-4 mr-2" /> Baixar Contrato
            </Button>
          )}
        </div>
      );
    }

    return null;
  };

  const renderKitStep = (attachment: Attachment) => {
    if (attachment.situacao === 'pago') {
      return (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-green-800">{attachment.description || 'Kit pago com sucesso'}</span>
        </div>
      );
    }

    if (attachment.situacao === 'aguardando_pagamento' || checkoutData) {
      return (
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              Checkout em andamento. Complete o pagamento ou escolha outro kit.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-primary hover:bg-primary-hover"
              onClick={() => checkKitPayed(attachment)}
              disabled={actionLoading === `kit-check-${attachment.id}`}
            >
              {actionLoading === `kit-check-${attachment.id}` ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Já paguei
            </Button>
            <Button variant="outline" size="sm" onClick={goToCheckout}>
              <ShoppingCart className="w-4 h-4 mr-2" /> Carrinho
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openHelpKit(attachment.description || 'Kit')}
            >
              <HelpCircle className="w-4 h-4 mr-2" /> Ajuda
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={() => changeKit(attachment)}
              disabled={actionLoading === `change-kit-${attachment.id}`}
            >
              {actionLoading === `change-kit-${attachment.id}` && (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              )}
              Trocar Kit
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm text-foreground">
            Adquira seu Kit Inicial de Revendedora escolhendo uma das opções disponíveis abaixo. Pagamento via pix ou cartão de crédito.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {kits.map((kit) => {
            const images = getKitImages(kit);
            const imgIndex = currentImageIndex[kit.id] || 0;

            return (
              <Card key={kit.id} className="overflow-hidden border-primary/10 hover:border-primary/30 transition-colors">
                {images.length > 0 && (
                  <div className="relative bg-muted">
                    <img
                      src={getKitImageUrl(kit, imgIndex)}
                      alt={kit.name}
                      className="w-full h-44 object-cover"
                    />
                    {images.length > 1 && (
                      <div className="absolute bottom-2 right-2 flex gap-1">
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7 rounded-full shadow-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            prevImage(kit.id);
                          }}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7 rounded-full shadow-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            nextImage(kit.id);
                          }}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <CardContent className="p-4 space-y-3">
                  <h4 className="font-semibold text-foreground">{kit.name}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {kit.description}
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {formatCurrency(kit.price)}
                  </p>
                  <Button
                    className="w-full bg-primary hover:bg-primary-hover"
                    onClick={() => openKitDetail(kit, attachment)}
                  >
                    Escolher
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="px-4 pt-6 pb-2">
        <div className="mb-1">
          <h1 className="text-2xl font-bold text-foreground">
            {`Oi${firstName ? ', ' + firstName : ''}!`}
          </h1>
          <p className="text-muted-foreground text-sm">
            Você está a poucos passos de se tornar uma Revendedora X! Prossiga com os passos abaixo para concluir seu cadastro.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="px-4 space-y-4">
          {/* Step 1: Completar Cadastro */}
          <Card
            className={`transition-all duration-300 ${
              cadastroCompleted ? 'border-green-200 bg-green-50/30' : ''
            }`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 ${
                    cadastroCompleted
                      ? 'bg-green-500'
                      : 'bg-gradient-to-br from-primary to-primary-hover'
                  }`}
                >
                  {cadastroCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <MapPin className="w-5 h-5" />
                  )}
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-base">Completar Cadastro</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Insira sua chave PIX e endereço
                  </CardDescription>
                </div>
                {cadastroCompleted && (
                  <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto shrink-0">
                    Concluído
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              {renderCadastroStep()}
            </CardContent>
          </Card>

          {/* Steps 2+: Attachment steps (contrato, kit, etc.) */}
          {attachmentsList.map((attachment, index) => {
            const StepIcon = getStepIcon(attachment);
            const completed = isStepCompleted(attachment);
            const unlocked = isStepUnlocked(index);

            return (
              <Card
                key={attachment.id}
                className={`transition-all duration-300 ${
                  !unlocked ? 'opacity-50 scale-[0.98]' : ''
                } ${completed ? 'border-green-200 bg-green-50/30' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 ${
                        completed
                          ? 'bg-green-500'
                          : unlocked
                          ? 'bg-gradient-to-br from-primary to-primary-hover'
                          : 'bg-muted-foreground/30'
                      }`}
                    >
                      {completed ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base">
                        {attachment.attachmentType === 'contrato' ? 'Assinar Contrato de Revendedora' : attachment.name}
                      </CardTitle>
                      {attachment.description && (
                        <CardDescription className="text-xs mt-0.5">
                          {attachment.description}
                        </CardDescription>
                      )}
                    </div>
                    {completed && (
                      <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto shrink-0">
                        Concluído
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                {unlocked && !completed ? (
                  <CardContent className="pt-2">
                    {attachment.attachmentType === 'contrato' &&
                      renderContractStep(attachment)}
                    {attachment.attachmentType === 'recibo_kit' &&
                      renderKitStep(attachment)}
                  </CardContent>
                ) : unlocked && completed ? (
                  <CardContent className="pt-2">
                    {attachment.attachmentType === 'contrato' ? (
                      renderContractStep(attachment)
                    ) : (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          {attachment.description || 'Etapa concluída com sucesso!'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                ) : !unlocked ? (
                  <CardContent className="pt-0">
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="text-muted-foreground text-sm">
                        Aguardando passo anterior
                      </p>
                    </div>
                  </CardContent>
                ) : null}
              </Card>
            );
          })}

          {totalSteps > 0 && (
            <Card
              className={`transition-all duration-300 ${
                !allStepsCompleted ? 'opacity-50 scale-[0.98]' : 'border-green-200 bg-green-50/30'
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 ${
                      allStepsCompleted
                        ? 'bg-green-500'
                        : 'bg-muted-foreground/30'
                    }`}
                  >
                    {allStepsCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <PartyPopper className="w-5 h-5" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <CardTitle className="text-base">Confirmação do Cadastro</CardTitle>
                  </div>
                  {allStepsCompleted && (
                    <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto shrink-0">
                      Concluído
                    </Badge>
                  )}
                </div>
              </CardHeader>

              {allStepsCompleted ? (
                <CardContent className="pt-2">
                  <div className="space-y-4">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        Seu cadastro está completo! Agora enviaremos as peças consignadas para você iniciar a revenda!
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate('/')}
                      className="bg-green-600 hover:bg-green-700 gap-2"
                    >
                      Ir para o Início <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-0">
                  <div className="p-3 bg-muted/50 rounded-lg text-center">
                    <p className="text-muted-foreground text-sm">
                      Aguardando passo anterior
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      <div className="h-20 lg:h-0"></div>

      <Sheet open={kitDetailOpen} onOpenChange={setKitDetailOpen}>
        <SheetContent side="bottom" className="h-[80vh] overflow-y-auto rounded-t-2xl">
          {kitDetailData && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="text-xl">{kitDetailData.name}</SheetTitle>
                <SheetDescription>{kitDetailData.description}</SheetDescription>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                {getKitImages(kitDetailData).length > 0 && (
                  <div className="relative rounded-xl overflow-hidden">
                    <img
                      src={getKitImageUrl(
                        kitDetailData,
                        currentImageIndex[kitDetailData.id] || 0
                      )}
                      alt={kitDetailData.name}
                      className="w-full rounded-xl object-cover max-h-72"
                    />
                    {getKitImages(kitDetailData).length > 1 && (
                      <>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full shadow-lg h-9 w-9"
                          onClick={() => prevImage(kitDetailData.id)}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full shadow-lg h-9 w-9"
                          onClick={() => nextImage(kitDetailData.id)}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                      </>
                    )}
                  </div>
                )}

                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    {formatCurrency(kitDetailData.price)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Pagamento via PIX ou Cartão</p>
                </div>

                <Button
                  className="w-full h-12 text-base bg-primary hover:bg-primary-hover"
                  onClick={() => kitDetailAttachment && createCheckout(kitDetailAttachment)}
                  disabled={actionLoading === 'checkout'}
                >
                  {actionLoading === 'checkout' ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <ShoppingCart className="w-5 h-5 mr-2" />
                  )}
                  Comprar este Kit
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PrimeiroAcesso;
